import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import CowAvatar from '../components/CowAvatar';
import CowLoader from '../components/CowLoader';

export default function UserProfilePage({ byEncoded }) {
  const { id, encoded } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const resolvedId = byEncoded ? atob(encoded) : id;

  const loadProfile = async () => {
    try {
      const res = await api.get(`/users/${resolvedId}/public`);
      setProfile(res.data);
    } catch {
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, [resolvedId]);

  const sendRequest = async () => {
    setActionLoading(true);
    try {
      await api.post(`/friends/request/${id}`);
      setProfile(prev => ({
        ...prev,
        friendship: { status: 'PENDING', isRequester: true },
      }));
    } finally {
      setActionLoading(false);
    }
  };

  const acceptRequest = async () => {
    setActionLoading(true);
    try {
      await api.post(`/friends/accept/${profile.friendship.friendshipId}`);
      setProfile(prev => ({
        ...prev,
        friendship: { ...prev.friendship, status: 'ACCEPTED' },
      }));
    } finally {
      setActionLoading(false);
    }
  };

  const removeFriend = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/friends/${profile.friendship.friendshipId}`);
      setProfile(prev => ({
        ...prev,
        friendship: { status: 'NONE' },
      }));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <CowLoader text="Cargando perfil..." />;
  if (!profile) return null;

  const { friendship, sharedVaquitas, mutualFriends = [] } = profile;

  const renderFriendAction = () => {
    if (friendship.status === 'NONE') {
      return (
        <button className="friend-btn add" onClick={sendRequest} disabled={actionLoading}>
          + Agregar amigo
        </button>
      );
    }
    if (friendship.status === 'ACCEPTED') {
      return (
        <button className="friend-btn remove" onClick={removeFriend} disabled={actionLoading}>
          Eliminar amigo
        </button>
      );
    }
    if (friendship.status === 'PENDING' && friendship.isRequester) {
      return <span className="friend-status pending">Solicitud enviada</span>;
    }
    if (friendship.status === 'PENDING' && !friendship.isRequester) {
      return (
        <button className="friend-btn add" onClick={acceptRequest} disabled={actionLoading}>
          Aceptar solicitud
        </button>
      );
    }
    return null;
  };

  return (
    <div className="user-profile-page">
      <button className="back-btn" onClick={() => navigate(-1)}>← Volver</button>

      <div className="profile-card">
        <div className="avatar-wrap">
          <CowAvatar seed={profile.profilePicture || profile.id} size={90} />
        </div>
        <h2 className="profile-name">{profile.name}</h2>
        <div className="friend-action">{renderFriendAction()}</div>
      </div>

      {sharedVaquitas.length > 0 && (
        <div className="shared-section">
          <h3 className="shared-title">Vaquitas en común</h3>
          <div className="vaquita-list">
            {sharedVaquitas.map(v => (
              <Link
                key={v.id}
                to={`/vaquita/code/${v.inviteCode}`}
                className="vaquita-card"
              >
                <span className="vaquita-icon">🐮</span>
                <span className="vaquita-name">{v.name}</span>
                <span className={`vaquita-status ${v.status === 'ACTIVE' ? 'active' : 'closed'}`}>
                  {v.status === 'ACTIVE' ? 'Activa' : 'Cerrada'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {sharedVaquitas.length === 0 && (
        <p className="no-shared">No tienen vaquitas en común todavía.</p>
      )}

      {mutualFriends.length > 0 && (
        <div className="shared-section">
          <h3 className="shared-title">Amigos en común</h3>
          <div className="mutual-friends-list">
            {mutualFriends.map(f => (
              <div key={f.id} className="mutual-friend-row" onClick={() => navigate(`/user/${f.id}`)}>
                <CowAvatar seed={f.profilePicture || f.id} size={34} />
                <span className="mutual-friend-name">{f.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <style jsx>{`
        .user-profile-page {
          max-width: 500px;
          margin: 0 auto;
          padding: 1.5rem 1rem 80px;
        }

        .back-btn {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 0.95rem;
          cursor: pointer;
          padding: 0.4rem 0;
          margin-bottom: 1.25rem;
          display: block;
        }

        .back-btn:hover { color: var(--text-primary); }

        .profile-card {
          background: var(--bg-secondary);
          border-radius: 16px;
          padding: 2rem 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 4px 20px var(--shadow-color);
          margin-bottom: 1.5rem;
        }

        .avatar-wrap {
          /* CowAvatar has overflow:visible for hats, so give a little top padding */
          padding-top: 1.5rem;
        }

        .profile-name {
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
        }

        .friend-action {
          margin-top: 0.25rem;
        }

        .friend-btn {
          padding: 0.55rem 1.5rem;
          border: none;
          border-radius: 20px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .friend-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .friend-btn:hover:not(:disabled) { opacity: 0.85; }

        .friend-btn.add {
          background: var(--accent-color);
          color: white;
        }

        .friend-btn.remove {
          background: transparent;
          color: var(--danger-color);
          border: 1.5px solid var(--danger-color);
        }

        .friend-status {
          font-size: 0.88rem;
          padding: 0.4rem 1rem;
          border-radius: 20px;
        }

        .friend-status.pending {
          background: var(--bg-primary);
          color: var(--text-muted);
          border: 1px solid var(--border-color);
        }

        .shared-title {
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 700;
          margin: 0 0 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-size: 0.78rem;
          color: var(--text-muted);
        }

        .vaquita-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .vaquita-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: var(--bg-secondary);
          border-radius: 10px;
          padding: 0.75rem 1rem;
          text-decoration: none;
          box-shadow: 0 1px 4px var(--shadow-color);
          transition: background 0.15s;
        }

        .vaquita-card:hover { background: var(--bg-hover, var(--bg-primary)); }

        .vaquita-icon { font-size: 1.2rem; }

        .vaquita-name {
          flex: 1;
          color: var(--text-primary);
          font-weight: 500;
          font-size: 0.95rem;
        }

        .vaquita-status {
          font-size: 0.78rem;
          padding: 0.2rem 0.6rem;
          border-radius: 10px;
          font-weight: 500;
        }

        .vaquita-status.active {
          background: #d1fae5;
          color: #065f46;
        }

        .vaquita-status.closed {
          background: var(--bg-primary);
          color: var(--text-muted);
        }

        .no-shared {
          color: var(--text-muted);
          text-align: center;
          padding: 1.5rem 0;
          font-size: 0.9rem;
        }

        .mutual-friends-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .mutual-friend-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.6rem 0.75rem;
          background: var(--bg-secondary);
          border-radius: 10px;
          cursor: pointer;
          box-shadow: 0 1px 4px var(--shadow-color);
          transition: background 0.15s;
        }

        .mutual-friend-row:hover { background: var(--bg-hover, var(--bg-primary)); }

        .mutual-friend-name {
          color: var(--text-primary);
          font-weight: 500;
          font-size: 0.92rem;
        }
      `}</style>
    </div>
  );
}
